import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow, Background, BackgroundVariant, Controls, MiniMap, addEdge,
  applyEdgeChanges, applyNodeChanges, Handle, Position, ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { GlassCard, TagInput } from "../gc/ui";
import { flattenEntities, DEFAULT_KANBAN, allTags, debtKanbanCards } from "../../lib/gamecraft";
import { Plus, Sparkle } from "@phosphor-icons/react";
import { studioHealth } from "../../lib/studioHealth";

const COLORS = {
  characters: "#3B82F6", npcs: "#8B5CF6", mobs: "#F59E0B", enemies: "#EF4444",
  minibosses: "#F97316", bosses: "#DC2626", items: "#10B981", articles: "#14B8A6",
  weapons: "#EAB308", armor: "#6366F1", story: "#7c6cff",
};

function EntityNode({ data }) {
  const color = COLORS[data.kind] || "#4c7dff";
  return (
    <div className="rounded-2xl overflow-hidden shadow-md min-w-[160px] bg-white border" style={{ borderColor: color }}>
      <Handle type="target" position={Position.Left} style={{ background: color }} />
      <div className="px-2 py-1 text-[10px] font-bold uppercase text-white" style={{ background: color }}>{data.kind}</div>
      <div className="px-3 py-2 text-sm font-semibold text-slate-800">{data.label}</div>
      {data.tags?.length > 0 && <div className="px-3 pb-2 text-[10px] text-slate-500">{data.tags.map((t) => `#${t}`).join(" ")}</div>}
      <Handle type="source" position={Position.Right} style={{ background: color }} />
    </div>
  );
}
const nodeTypes = { entity: EntityNode };

function Inner({ project, onChange }) {
  const kanban = project.kanban?.columns ? project.kanban : DEFAULT_KANBAN;
  const initial = project.diagram || { nodes: [], edges: [] };
  const [nodes, setNodes] = useState(initial.nodes || []);
  const [edges, setEdges] = useState(initial.edges || []);
  const [filterTag, setFilterTag] = useState("");
  const tags = allTags(project);

  useEffect(() => {
    onChange({ diagram: { nodes, edges } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges]);

  const onNodesChange = useCallback((c) => setNodes((n) => applyNodeChanges(c, n)), []);
  const onEdgesChange = useCallback((c) => setEdges((e) => applyEdgeChanges(c, e)), []);
  const onConnect = useCallback((p) => setEdges((e) => addEdge({ ...p, animated: true, style: { stroke: "#4c7dff" } }, e)), []);

  const generate = () => {
    const ents = flattenEntities(project);
    const stories = project.story_items || [];
    const next = [];
    ents.forEach((e, i) => {
      next.push({
        id: `${e.kind}-${e.id}`,
        type: "entity",
        position: { x: 40 + (i % 4) * 220, y: 40 + Math.floor(i / 4) * 110 },
        data: { kind: e.kind, label: e.name, tags: e.tags || [], entityId: e.id },
      });
    });
    stories.forEach((s, i) => {
      next.push({
        id: `story-${s.id}`,
        type: "entity",
        position: { x: 40 + (i % 4) * 220, y: 360 + Math.floor(i / 4) * 110 },
        data: { kind: "story", label: s.name, tags: s.tags || [], entityId: s.id },
      });
    });
    setNodes(next);
  };

  const setKanban = (columns) => onChange({ kanban: { columns } });

  const addCard = (colId) => {
    const title = window.prompt("Tarea") || "Nueva tarea";
    setKanban(kanban.columns.map((c) => c.id === colId ? {
      ...c,
      cards: [...(c.cards || []), { id: crypto.randomUUID(), title, tags: filterTag ? [filterTag] : [], status: "neutral" }],
    } : c));
  };

  const moveCard = (cardId, toCol) => {
    let card;
    const stripped = kanban.columns.map((c) => {
      const found = (c.cards || []).find((x) => x.id === cardId);
      if (found) card = found;
      return { ...c, cards: (c.cards || []).filter((x) => x.id !== cardId) };
    });
    if (!card) return;
    setKanban(stripped.map((c) => c.id === toCol ? { ...c, cards: [...c.cards, card] } : c));
  };

  const visibleNodes = useMemo(() => {
    if (!filterTag) return nodes;
    return nodes.filter((n) => (n.data?.tags || []).includes(filterTag));
  }, [nodes, filterTag]);

  const relatedCards = useMemo(() => {
    if (!filterTag) return kanban.columns;
    return kanban.columns.map((c) => ({ ...c, cards: (c.cards || []).filter((card) => (card.tags || []).includes(filterTag) || !filterTag) }));
  }, [kanban, filterTag]);

  return (
    <div className="h-full flex flex-col lg:flex-row min-h-0">
      <div className="flex-1 min-h-[360px] relative">
        <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-2">
          <button className="gc-btn gc-btn-primary h-8 px-3 text-xs" onClick={generate}><Sparkle size={12} /> Generar desde ideas</button>
          <button
            className="gc-btn gc-btn-ghost h-8 px-3 text-xs"
            onClick={() => {
              const cards = debtKanbanCards(project);
              setKanban(kanban.columns.map((c) => c.id === "todo" ? { ...c, cards: [...(c.cards || []), ...cards] } : c));
            }}
          >
            Deuda de sprites
          </button>
          <select className="gc-input h-8 px-2 text-xs" value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
            <option value="">Todas las etiquetas</option>
            {tags.map((t) => <option key={t} value={t}>#{t}</option>)}
          </select>
        </div>
        <ReactFlow
          nodes={visibleNodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
        >
          <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
          <MiniMap />
          <Controls />
        </ReactFlow>
      </div>
      <aside className="lg:w-[360px] border-t lg:border-t-0 lg:border-l p-3 overflow-auto gf-scroll" style={{ borderColor: "var(--gc-border)", background: "var(--gc-surface-soft)" }}>
        <div className="font-semibold mb-3">Tareas relacionadas</div>
        <div className="grid grid-cols-1 gap-3">
          {relatedCards.map((col) => (
            <GlassCard key={col.id} className="p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--gc-muted)" }}>{col.title}</span>
                <button onClick={() => addCard(col.id)} className="text-sky-500"><Plus size={14} /></button>
              </div>
              <div className="space-y-2">
                {(col.cards || []).map((card) => (
                  <div
                    key={card.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("card", card.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); moveCard(e.dataTransfer.getData("card"), col.id); }}
                    className="rounded-xl p-2 text-sm"
                    style={{ background: "var(--gc-surface-strong)", border: "1px solid var(--gc-border)" }}
                  >
                    <div className="font-medium">{card.title}</div>
                    <TagInput tags={card.tags || []} onChange={(tags) => {
                      setKanban(kanban.columns.map((c) => ({
                        ...c,
                        cards: (c.cards || []).map((x) => x.id === card.id ? { ...x, tags } : x),
                      })));
                    }} />
                    {col.id !== "done" && (
                      <button className="text-[10px] text-sky-500 mt-1" onClick={() => moveCard(card.id, col.id === "todo" ? "doing" : "done")}>Mover →</button>
                    )}
                  </div>
                ))}
                {(col.cards || []).length === 0 && (
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); moveCard(e.dataTransfer.getData("card"), col.id); }}
                    className="text-[11px] py-4 text-center"
                    style={{ color: "var(--gc-faint)" }}
                  >
                    Suelta aquí
                  </div>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      </aside>
    </div>
  );
}

export default function CanvasKanbanPanel({ project, onChange }) {
  const score = studioHealth(project).modules.find((m) => m.id === "canvas")?.score ?? 0;
  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="px-4 pt-3 pb-2 flex flex-wrap items-center gap-2 shrink-0">
        <h2 className="font-display text-lg font-semibold">Canvas & Kanban</h2>
        <span className="gc-chip tabular-nums">{score}%</span>
        <span className="text-xs" style={{ color: "var(--gc-muted)" }}>
          Nuevo · genera desde Ideas. Experto · arrastra tarjetas entre columnas.
        </span>
      </div>
      <div className="flex-1 min-h-0">
        <ReactFlowProvider>
          <Inner project={project} onChange={onChange} />
        </ReactFlowProvider>
      </div>
    </div>
  );
}
